import { useState } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search } from 'lucide-react';
import { kycBadge } from '@/components/sales/SalesClientDetail';

const TH = 'text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-brand-text-secondary';

export function ClientMappingTab({ clients, people, reload }) {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const assign = async (clientId, spId, current) => {
    if (!spId || spId === (current || 'none')) return;
    try {
      await api.put(`/sales/clients/${clientId}/assign`, { sales_person_id: spId === 'none' ? null : spId });
      toast.success('Mapping updated'); reload();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const rows = clients.filter(c => {
    if (filter === 'unmapped' && c.sales_person_id) return false;
    if (filter !== 'all' && filter !== 'unmapped' && c.sales_person_id !== filter) return false;
    return `${c.name} ${c.client_code} ${c.email}`.toLowerCase().includes(q.toLowerCase());
  });
  return (
    <Card className="border border-brand-border bg-white">
      <div className="p-3 flex flex-wrap gap-2 border-b border-brand-border">
        <div className="relative w-64"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-text-secondary" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search client" className="pl-8 h-9" data-testid="mapping-search" /></div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-56 h-9" data-testid="mapping-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            <SelectItem value="unmapped">Not mapped</SelectItem>
            {people.map(p => <SelectItem key={p.id} value={p.id}>{p.sales_code} · {p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="mapping-table">
          <thead><tr className="border-b border-brand-border bg-brand-bg/50">
            {['Client ID', 'Name', 'KYC', 'Sales Person'].map(h => <th key={h} className={TH}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={4} className="py-12 text-center text-brand-text-secondary">No clients</td></tr> : rows.map(c => (
              <tr key={c.id} className="border-b border-brand-border/50">
                <td className="py-2.5 px-4 font-mono text-xs text-brand-primary">{c.client_code}</td>
                <td className="py-2.5 px-4"><p className="font-medium">{c.name}</p><p className="text-xs text-brand-text-secondary">{c.email}</p></td>
                <td className="py-2.5 px-4">{kycBadge(c.kyc_status)}</td>
                <td className="py-2.5 px-4">
                  <Select value={c.sales_person_id || 'none'} onValueChange={v => assign(c.id, v, c.sales_person_id)}>
                    <SelectTrigger className="w-60 h-8 text-xs" data-testid={`map-select-${c.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Not mapped —</SelectItem>
                      {people.map(p => <SelectItem key={p.id} value={p.id}>{p.sales_code} · {p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
